import { captureException } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import type { IPlayer } from "../../entities/player.entity";
import type { IVideo } from "../../entities/video.entity";
import {
	getPlayerByPassword,
	listCategories,
	playerActivationStore,
	queryVideos,
} from "../../utils/firebase";
import { useVideoDownloadErrorStore } from "../../utils/store";
import {
	createImageCache,
	createVideoCache,
	isVideoCached,
	migrateVideoUrl,
} from "../../utils/videoCache";

type IVideoWithCache = IVideo & {
	migratedVideoUrl: string;
};

export const useCurrentPlayer = () => {
	return useQuery({
		queryKey: ["player", "current", playerActivationStore.activationPassword],
		queryFn: async () => {
			const playerPassword = playerActivationStore.activationPassword;

			if (!playerPassword) {
				return Promise.resolve(null);
			}

			return getPlayerByPassword(playerPassword);
		},
	});
};

export const startPreloadingEveryVideo = async ({
	onProgress,
	onComplete,
	onAlreadyCached,
	player,
}: {
	player: IPlayer;
	onProgress: (progress: {
		total: number;
		loadedVideos: IVideo[];
		loaded: number;
	}) => void;
	onComplete: (failedCount: number, downloadedCount: number) => void;
	onAlreadyCached: () => void;
}) => {
	console.log("startPreloadingEveryVideo", "player.groups", player.groups);

	const allVideos: IVideoWithCache[] = (await queryVideos(player.groups)).map(
		(video) => ({
			...video,
			migratedVideoUrl: migrateVideoUrl(video.video.url),
		}),
	);

	// Scan the local cache before touching the network, and report it right
	// away: a network blip must never make a fully cached kiosk report zero.
	const uncachedVideos: IVideoWithCache[] = [];
	const cachedVideos: IVideoWithCache[] = [];

	for (const video of allVideos) {
		if (await isVideoCached(video.migratedVideoUrl)) {
			cachedVideos.push(video);
			continue;
		}

		uncachedVideos.push(video);
	}

	const uncachedVideosCount = uncachedVideos.length;
	let downloadedCount = 0;
	let failedCount = 0;

	onProgress({
		total: uncachedVideosCount,
		loadedVideos: cachedVideos,
		loaded: downloadedCount,
	});

	// Images are nice to have offline; a failed image is not a failed preload.
	const cacheImage = async (url: string | undefined) => {
		try {
			await createImageCache(url);
		} catch (error) {
			captureException(error);
		}
	};

	for (const category of await listCategories()) {
		if (!category.bgImage || !category.bgImage.url) {
			captureException(new Error(`Category without bgImage: ${category.id}`));
			continue;
		}
		await cacheImage(category.bgImage.url);
	}

	for (const video of cachedVideos) {
		await cacheImage(video.thumbnail?.url);
	}

	if (uncachedVideos.length === 0) {
		onAlreadyCached();
		return;
	}

	// New preload run: previous failures get a fresh retry
	useVideoDownloadErrorStore.getState().clearFailedVideoDownloads();

	for (const video of uncachedVideos) {
		try {
			await createVideoCache(video.migratedVideoUrl);
			await cacheImage(video.thumbnail?.url);
			downloadedCount++;
			cachedVideos.push(video);

			onProgress({
				total: uncachedVideosCount,
				loaded: downloadedCount,
				loadedVideos: cachedVideos,
			});
		} catch (error) {
			failedCount++;
			captureException(error);
			useVideoDownloadErrorStore.getState().addFailedVideoDownload(video.title);
			console.error("Falha ao cachear vídeo:", video.title, error);
		}
	}

	// Always report completion so total failure is visible on screen too
	onComplete(failedCount, downloadedCount);
};

export function useVideosPreload() {
	const { enqueueSnackbar } = useSnackbar();

	const [videosPreloading, setVideosPreloading] = useState({
		total: 0,
		loadedVideos: [] as IVideo[],
		loaded: 0,
	});
	// Survives a re-run of the query: the ping keeps reporting the last known
	// cached list instead of an empty one while a new scan is in progress.
	const lastGood = useRef(videosPreloading);

	const currentPlayer = useCurrentPlayer();

	const preload = useQuery({
		queryKey: ["videos", "preloading", currentPlayer.data?.id],
		enabled: Boolean(currentPlayer.data),
		// A run that dies on a network blip is retried, and again on reconnect.
		retry: 5,
		retryDelay: (attempt) => Math.min(30_000 * 2 ** attempt, 10 * 60_000),
		refetchOnReconnect: true,
		async queryFn() {
			if (!window.api?.isInstalledPlayer) {
				return Promise.resolve(true);
			}

			await startPreloadingEveryVideo({
				player: currentPlayer.data!,
				onProgress: (progress) => {
					lastGood.current = progress;
					setVideosPreloading(progress);
				},
				onComplete: (failedCount: number, downloadedCount: number) => {
					if (failedCount > 0 && downloadedCount === 0) {
						enqueueSnackbar({
							message: `Nenhum vídeo pôde ser baixado (${failedCount} falhas)`,
							variant: "error",
						});
					} else if (failedCount > 0) {
						enqueueSnackbar({
							message: `Vídeos carregados (${failedCount} não disponíveis offline)`,
							variant: "warning",
						});
					} else {
						enqueueSnackbar({
							message: "Vídeos carregados com sucesso!",
							variant: "success",
						});
					}
				},
				onAlreadyCached: () => {
					console.log("videos already cached");
					enqueueSnackbar({
						message: "Vídeos já carregados!",
						variant: "success",
					});
				},
			});

			return Promise.resolve(true);
		},
		onError: captureException,
	});

	const current =
		videosPreloading.loadedVideos.length > 0
			? videosPreloading
			: lastGood.current;

	return {
		isFetching: preload.isFetching,
		isFetched: preload.isFetched,
		total: current.total,
		loaded: current.loaded,
		loadedVideos: current.loadedVideos,
	};
}
